import { ChangeEvent, useContext, useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { PricingSheet } from '../../../../../shared/types';

import NotificationProvider from '../../../providers/Notification/NotificationProvider';
import { Alert, TextField, Typography } from '@mui/material';
import { isValidEmail } from '../../../utils/isValidEmail';
import { updatePricingSheetById } from '../../../services/pricingSheets';
import { Box } from '@mui/system';

export interface NewVersionDialogProps {
  pricingSheet: PricingSheet;
  open: boolean;
  title: string;
  updateSheets: () => void;
  onClose: () => void;
}

const DataField: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography color={'text.secondary'}>{label}</Typography>
      <Typography sx={{ ml: 2 }} color={'text.primary'}>
        {value}
      </Typography>
    </Box>
  );
};

const ResponsiblePersonDialog = (props: NewVersionDialogProps) => {
  const { pricingSheet, open, title, onClose, updateSheets } = props;
  const { setNotification } = useContext(NotificationProvider);
  const [email, setEmail] = useState('');
  const handleEmailChange = (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    setEmail(event.target.value);
  };

  const [sellerPhoneNumber, setSellerPhoneNumber] = useState('');
  const handlePhoneNumberChange = (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    setSellerPhoneNumber(event.target.value);
  };

  const [sellerName, setSellerName] = useState('');
  const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSellerName(event.target.value);
  };

  const [sellerEmail, setSellerEmail] = useState('');
  const handleSellerEmailChange = (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    setSellerEmail(event.target.value);
  };

  const validEmail = isValidEmail(email);
  const validSellerEmail = isValidEmail(sellerEmail);

  const onClickSaveResponsiblePerson = async (
    pricingSheet: PricingSheet,
    responsiblePerson: string,
    sellerName?: string,
    sellerPhoneNumber?: string,
    sellerEmail?: string,
  ) => {
    const newValues = {
      responsiblePerson,
      sellerName,
      sellerPhoneNumber,
      sellerEmail,
    };

    const filtered = Object.fromEntries(
      Object.entries(newValues).filter(
        ([_, value]) =>
          value !== null && value !== undefined && value !== '',
      ),
    );

    try {
      await updatePricingSheetById(pricingSheet.id, {
        ...pricingSheet,
        ...filtered,
      });
      updateSheets();
      setNotification({
        type: 'SNACKBAR',
        duration: 3000,
        severity: 'success',
        message: `Hinnaston ${pricingSheet.name} vastuuhenkilö päivitetty onnistuneesti!`,
      });
    } catch (error) {
      setNotification({
        type: 'SNACKBAR',
        duration: 3000,
        severity: 'error',
        message: `Palvelimella tapahtui virhe! Vastuuhenkilön päivittäminen epäonnistui!`,
      });
    } finally {
      setEmail('');
      setSellerPhoneNumber('');
      setSellerName('');
      setSellerEmail('');

      onClose();
    }
  };

  useEffect(() => {
    return () => {
      setEmail('');
    };
  }, []);
  return (
    <Dialog disableScrollLock open={open} onClose={onClose}>
      <DialogTitle id="new-version-dialog-title">{title}</DialogTitle>
      <DialogContent>
        <Alert severity="info">
          <b>Vinkki!</b> Kentät voi jättää myös tyhjäksi, jolloin
          nykyinen tieto säilyy. Ainoastaan täytettyjen kenttien tieto
          muuttuu.
        </Alert>
        <DialogContentText>
          <DataField
            label={'Tekijä'}
            value={pricingSheet.userEmail}
          />
          <DataField
            label={'Nykyinen vastuuhenkilö'}
            value={pricingSheet.responsiblePerson ?? '-'}
          />

          <TextField
            sx={{ width: '100%', mb: 1, mt: 3 }}
            size="small"
            id="approval-email"
            label="Uusi vastuuhenkilö (sähköpostiosoite)"
            variant="outlined"
            type="email"
            value={email}
            error={
              !email ? false : !validEmail || !email ? true : false
            }
            helperText={
              !email
                ? ''
                : !validEmail || !email
                  ? 'Varmista että sähköpostiosoite on oikein'
                  : null
            }
            onChange={handleEmailChange}
          />

          <h4>Yhteyshenkilön tiedot (näkyy tulosteella)</h4>
          <DataField
            label={'Yhteyshenkilö'}
            value={
              pricingSheet.sellerName ||
              pricingSheet.userFullName ||
              '-'
            }
          />
          <DataField
            label={'Yhteyshenkilön puhelinnumero'}
            value={
              pricingSheet.sellerPhoneNumber ||
              pricingSheet.userPhoneNumber ||
              '-'
            }
          />
          <DataField
            label={'Yhteyshenkilön sähköposti'}
            value={
              pricingSheet.sellerEmail ||
              pricingSheet.userEmail ||
              '-'
            }
          />
          <TextField
            sx={{ width: '100%', mb: 1, mt: 3 }}
            size="small"
            id="responsible-name"
            label="Uusi yhteyshenkilön nimi"
            variant="outlined"
            value={sellerName}
            onChange={handleNameChange}
          />
          <TextField
            sx={{ width: '100%', mb: 1, mt: 3 }}
            size="small"
            id="responsbile-phone"
            label="Uusi yhteyshenkilön puhelinnumero"
            variant="outlined"
            value={sellerPhoneNumber}
            onChange={handlePhoneNumberChange}
          />
          <TextField
            sx={{ width: '100%', mb: 1, mt: 3 }}
            size="small"
            id="approval-email"
            label="Uusi yhteyshenkilön sähköpostiosoite"
            variant="outlined"
            type="email"
            error={
              !sellerEmail
                ? false
                : !validSellerEmail || !sellerEmail
                  ? true
                  : false
            }
            helperText={
              !sellerEmail
                ? ''
                : !validSellerEmail || !sellerEmail
                  ? 'Varmista että sähköpostiosoite on oikein'
                  : null
            }
            value={sellerEmail}
            onChange={handleSellerEmailChange}
          />
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          color="inherit"
          onClick={() => {
            setEmail('');
            onClose();
          }}
        >
          Peruuta
        </Button>
        <Button
          onClick={() => {
            onClickSaveResponsiblePerson(
              pricingSheet,
              email,
              sellerName,
              sellerPhoneNumber,
              sellerEmail,
            );
          }}
          autoFocus
        >
          Tallenna
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ResponsiblePersonDialog;
